import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';

export default function ArticleDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4 block"></i>
            <p className="text-foreground-600 mb-4">文章未找到</p>
            <Link to="/articles" className="text-primary-500 hover:text-primary-600 text-sm font-medium whitespace-nowrap cursor-pointer">
              &larr; 返回文章列表
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const categoryName =
    article.category === 'beauty'
      ? '美容'
      : article.category === 'hair'
      ? '美发'
      : article.category === 'nail'
      ? '美甲'
      : '美体';

  const related = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.summary,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            datePublished: article.publishDate,
            image: article.image,
            publisher: {
              '@type': 'Organization',
              name: '91美',
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://91mei.com/article/${article.id}`,
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://91mei.com',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '美业资讯',
                item: 'https://91mei.com/articles',
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: article.title,
                item: `https://91mei.com/article/${article.id}`,
              },
            ],
          }),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16">
          <Breadcrumb
            items={[
              { label: t('breadcrumb.articles'), path: '/articles' },
              { label: article.title },
            ]}
          />

          <article className="py-8 md:py-12 px-4 md:px-6">
            <div className="max-w-[800px] mx-auto">
              <div className="mb-6">
                <span className="inline-block bg-accent-500 text-white text-xs px-3 py-1 rounded-full mb-4 whitespace-nowrap">
                  {categoryName}
                </span>
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4 leading-snug">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                  <span className="whitespace-nowrap">
                    <i className="ri-user-line mr-1"></i>
                    {article.author}
                  </span>
                  <span className="whitespace-nowrap">
                    <i className="ri-calendar-line mr-1"></i>
                    {article.publishDate}
                  </span>
                  <span className="whitespace-nowrap">
                    <i className="ri-eye-line mr-1"></i>
                    {article.readCount.toLocaleString()} 阅读
                  </span>
                </div>
              </div>

              <div className="mb-8 rounded-lg overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-auto object-cover object-top"
                />
              </div>

              <div
                className="article-content prose prose-sm max-w-none text-foreground-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 pt-8 border-t border-background-200">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-foreground-500 whitespace-nowrap">标签：</span>
                  <span className="text-xs bg-background-100 text-foreground-600 px-3 py-1 rounded-full whitespace-nowrap">
                    {categoryName}
                  </span>
                  <span className="text-xs bg-background-100 text-foreground-600 px-3 py-1 rounded-full whitespace-nowrap">
                    91美
                  </span>
                  <span className="text-xs bg-background-100 text-foreground-600 px-3 py-1 rounded-full whitespace-nowrap">
                    美业资讯
                  </span>
                </div>
              </div>
            </div>
          </article>

          {related.length > 0 && (
            <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
              <div className="max-w-[1280px] mx-auto">
                <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-8">
                  {t('articles.relatedArticles')}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {related.map((a) => (
                    <ArticleCard
                      key={a.id}
                      id={a.id}
                      title={a.title}
                      summary={a.summary}
                      author={a.author}
                      publishDate={a.publishDate}
                      readCount={a.readCount}
                      image={a.image}
                      category={
                        a.category === 'beauty'
                          ? '美容'
                          : a.category === 'hair'
                          ? '美发'
                          : a.category === 'nail'
                          ? '美甲'
                          : '美体'
                      }
                    />
                  ))}
                </div>
              </div>
            </section>
          )}
        </div>
        <Footer />
      </div>
    </>
  );
}